import axios, { InternalAxiosRequestConfig } from "axios";
import { Store } from "@reduxjs/toolkit";
import { environment } from "src/config/environment";
import { apiTokenRequest } from "../config/authConfig";
import { ensureMsalReady, msalInstance } from "./msalService";
import { teamsAuthService } from "./teamsService";
import { clearCognitoToken, ensureValidCognitoToken, forceRefreshCognitoToken } from "./cognitoAuth";
import { isCognitoLoginAllowed, isEntraEnabled } from "src/config/loginMethods";
import { setAccessForbidden } from "src/store/authSlice";

let store: Store;
export const injectStore = (_store: Store) => {
store = _store;
};

let isAuthenticating = false;
const isCognitoEnabled = isCognitoLoginAllowed;

const getLoginPath = (): string => {
const basePath = (environment.REACT_APP_BASE_PATH || "").replace(/\/$/, "");
return `${basePath}/login`;
};

const redirectToInAppLogin = (): void => {
if (typeof window === "undefined") return;
const loginPath = getLoginPath();
if (!window.location.pathname.endsWith("/login")) {
window.location.href = loginPath;
}
};

export const getAccessToken = async (): Promise<string | null> => {
try {
if (teamsAuthService.isRunningInTeams()) {
const teamsToken = await teamsAuthService.getTeamsToken();
if (teamsToken) return teamsToken;
}

await ensureMsalReady();
const accounts = msalInstance.getAllAccounts();
if (accounts.length === 0) return null;

const response = await msalInstance.acquireTokenSilent({
...apiTokenRequest,
account: accounts[0],
});
return response.accessToken;
} catch (error) {
console.error("Error acquiring access token:", error);
return null;
}
};

export const getBearerToken = async (): Promise<string | null> => {
if (isCognitoEnabled) {
const cognitoToken = await ensureValidCognitoToken();
if (cognitoToken) return cognitoToken;
}
return getAccessToken();
};

const axiosInstance = axios.create({
baseURL: environment.REACT_APP_API_URL,
});

axiosInstance.interceptors.request.use(
async (config: InternalAxiosRequestConfig) => {
const cognitoAccessToken = isCognitoEnabled ? await ensureValidCognitoToken() : null;
if (cognitoAccessToken) {
config.withCredentials = false;
config.headers.Authorization = `Bearer ${cognitoAccessToken}`;
} else {
config.withCredentials = true;
const token = await getAccessToken();
if (token) {
config.headers.Authorization = `Bearer ${token}`;
config.headers["X-Auth-Source"] = teamsAuthService.isRunningInTeams() ? "teams-sso" : "azure-entra";
}
}
return config;
},
(error) => Promise.reject(error),
);

axiosInstance.interceptors.response.use(
(response) => response,
async (error) => {
const originalRequest = error.config;
console.error("API Error Status:", error.response?.status);

// KCC has no per-resource RBAC, so a 403 always means the signed-in user is not
// (or no longer) provisioned for the Control Center. Surface it globally so the
// Authorization gate replaces the shell with the access-denied screen — never
// let a page swallow it and leave the user "inside" the app.
if (error.response?.status === 403) {
try {
store?.dispatch(setAccessForbidden());
} catch {
// store not injected yet (very early boot) — non-fatal.
}
}

if (error.response?.status === 401 && !isEntraEnabled) {
if (isCognitoEnabled) {
if (originalRequest._retry) {
await clearCognitoToken();
redirectToInAppLogin();
return Promise.reject(error);
}
originalRequest._retry = true;
try {
const newToken = await forceRefreshCognitoToken();
if (!newToken) {
await clearCognitoToken();
redirectToInAppLogin();
return Promise.reject(error);
}
originalRequest.headers.Authorization = `Bearer ${newToken}`;
return axiosInstance(originalRequest);
} catch (refreshError) {
console.error("Cognito force refresh failed:", refreshError);
await clearCognitoToken();
redirectToInAppLogin();
return Promise.reject(error);
}
}
window.location.href = environment.REACT_APP_REDIRECT_URL || "";
return Promise.reject(error);
}

if (error.response?.status === 401 && isEntraEnabled) {
if (teamsAuthService.isRunningInTeams()) {
if (originalRequest._retry) {
console.error("Teams token refresh failed, user needs to re-authenticate");
return Promise.reject(error);
}
originalRequest._retry = true;
try {
const newToken = await teamsAuthService.getTeamsToken();
if (newToken) {
originalRequest.headers.Authorization = `Bearer ${newToken}`;
return axiosInstance(originalRequest);
}
} catch (teamsError) {
console.error("Failed to refresh Teams token:", teamsError);
return Promise.reject(error);
}
}

if (isAuthenticating) return Promise.reject(error);
if (originalRequest._retry) {
isAuthenticating = false;
return Promise.reject(error);
}
originalRequest._retry = true;

const inProgress = msalInstance.getActiveAccount() === null && sessionStorage.getItem("msal.interaction.status");
if (inProgress) return Promise.reject(error);

if (msalInstance) {
try {
await ensureMsalReady();
const accounts = msalInstance.getAllAccounts();
if (accounts.length === 0) {
isAuthenticating = true;
setTimeout(() => {
msalInstance.loginRedirect(apiTokenRequest).catch((redirectError) => {
console.error("Login redirect failed:", redirectError);
isAuthenticating = false;
});
}, 0);
return Promise.reject(error);
}
const response = await msalInstance.acquireTokenSilent({
...apiTokenRequest,
account: accounts[0],
forceRefresh: true,
});
isAuthenticating = false;
originalRequest.headers.Authorization = `Bearer ${response.accessToken}`;
return axiosInstance(originalRequest);
} catch (tokenError: any) {
console.error("Token acquisition failed:", tokenError);
if (
tokenError.errorCode === "interaction_required" ||
tokenError.errorCode === "consent_required" ||
tokenError.errorCode === "login_required"
) {
if (!isAuthenticating) {
isAuthenticating = true;
setTimeout(() => {
msalInstance.loginRedirect(apiTokenRequest).catch((redirectError) => {
console.error("Login redirect failed:", redirectError);
isAuthenticating = false;
});
}, 0);
}
} else {
isAuthenticating = false;
}
return Promise.reject(error);
}
} else {
if (!isAuthenticating) {
window.location.href = environment.REACT_APP_REDIRECT_URL || "";
}
}
}

return Promise.reject(error);
},
);

export default axiosInstance;